import { Icon, styled, theme } from 'design-system';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { UploadButtonNew } from 'preflight-core';
import React, { useEffect, useState } from 'react';

import { UploadState } from '../../utils/use-upload-document';

interface UploadErrorResponse {
  errorReason?: string;
  errorCode?: string;
}

interface Addon {
  id: string;
  title: string;
  description: string;
  icon: string;
  price: number;
  currency: string;
  includes?: string[];
  addOnServiceId?: number;
}

interface AddOnExpertProps {
  addons?: Addon[];
  enabledAddOnServices?: string[];
  onFileSelected: (file: File, addonId?: string) => void;
  uploadState: UploadState;
  setErrorSection: (section: string) => void;
  setOtherSectionClicked: (clicked: boolean) => void;
  errorSection: string;
  errorScreen?: boolean;
}

export const AddOnExpert = ({
  addons = [],
  onFileSelected,
  uploadState,
  setErrorSection,
  setOtherSectionClicked,
  errorSection,
  errorScreen = false,
}: AddOnExpertProps) => {
  const { t } = useTranslation(['homepage']);
  const [processingAddonId, setProcessingAddonId] = useState<string | null>(
    null,
  );
  const [processingTimeout, setProcessingTimeout] =
    useState<NodeJS.Timeout | null>(null);
  const [showAddonPopup, setShowAddonPopup] = useState(false);
  const [selectedAddonForPopup, setSelectedAddonForPopup] =
    useState<Addon | null>(null);
  const [showFAQ, setShowFAQ] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (uploadState.name === 'error' && processingAddonId) {
      if (processingTimeout) {
        clearTimeout(processingTimeout);
        setProcessingTimeout(null);
      }

      const addonErrorSection = `addon-${processingAddonId}`;
      setErrorSection(addonErrorSection);
    } else if (uploadState.name === 'success') {
      if (processingTimeout) {
        clearTimeout(processingTimeout);
        setProcessingTimeout(null);
      }

      setErrorSection('');
    }
  }, [uploadState.name, processingAddonId, setErrorSection, processingTimeout]);

  useEffect(() => {
    return () => {
      if (processingTimeout) {
        clearTimeout(processingTimeout);
      }
    };
  }, [processingTimeout]);

  const handleFileSelectedForAddon = (file: File, addonId: string) => {
    setErrorSection('');
    setOtherSectionClicked(false);

    if (processingTimeout) {
      clearTimeout(processingTimeout);
    }

    setProcessingAddonId(addonId);

    onFileSelected(file, addonId);
  };

  const handleToggleFAQ = (faqKey: string) => {
    setShowFAQ((prev) => ({ ...prev, [faqKey]: !prev[faqKey] }));
  };

  const handleCardClick = (addon: Addon, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const isUploadButton = target.closest(
      '[data-test-id*="addon-upload"], [data-test-id*="addon-retry"]',
    );

    if (!isUploadButton) {
      setSelectedAddonForPopup(addon);
      setShowAddonPopup(true);
    }
  };

  const getAddonIncludes = (addonId: string): string[] => {
    switch (addonId) {
      case 'generic_human_submission_support':
      case 'human_submission_support':
        return [
          t(
            'addon_expert.includes_options.submission_formatting',
            'Submission formatting',
          ),
          t(
            'addon_expert.portal_navigation_support',
            'Portal navigation support',
          ),
          t('addon_expert.cover_letter_template', 'Cover letter template'),
        ];
      case 'generic_human_proofreading':
        return [
          t('addon_expert.includes_options.review_ai_edits', 'Review AI-edits'),
          t(
            'addon_expert.includes_options.grammar_corrections',
            'Grammar corrections',
          ),
          t(
            'addon_expert.includes_options.implement_consistency',
            'Implement consistency',
          ),
        ];
      case 'art_work_formatting':
        return [
          t('artwork_card.card_artwork_formatting1', 'Colour optimisation'),
          t('artwork_card.card_artwork_formatting2', 'Resolution Compliance'),
          t('artwork_card.card_artwork_formatting3', 'Figure resizing'),
        ];
      default:
        return [
          t(
            'addon_expert.includes_options.submission_formatting',
            'Submission formatting',
          ),
          t(
            'addon_expert.portal_navigation_support',
            'Portal navigation support',
          ),
          t('addon_expert.cover_letter_template', 'Cover letter template'),
        ];
    }
  };

  const getAddonDescription = (addonId: string): string => {
    switch (addonId) {
      case 'generic_human_submission_support':
      case 'human_submission_support':
        return t(
          'addon_expert.descriptions.expert_submission',
          'Support to format and submit your manuscript completely and correctly.',
        );
      case 'generic_human_proofreading':
        return t(
          'addon_expert.descriptions.proofreading',
          'Expert-led proofread to improve grammar, clarity, and consistency.',
        );
      case 'art_work_formatting':
        return t(
          'addon_expert.descriptions.artwork_formatting',
          'Artwork formatting to meet journal standards and streamline the publishing process',
        );
      default:
        return t(
          'addon_expert.descriptions.default',
          'Professional service to enhance your manuscript',
        );
    }
  };

  const faqList = [
    {
      key: 'faq1',
      question: t('addon_popup.faq.faq1_title'),
      answer: t('addon_popup.faq.faq1_content'),
    },
    {
      key: 'faq2',
      question: t('addon_popup.faq.faq2_title'),
      answer: t('addon_popup.faq.faq2_content'),
    },
    {
      key: 'faq3',
      question: t('addon_popup.faq.faq3_title'),
      answer: t('addon_popup.faq.faq3_content'),
    },
    {
      key: 'faq4',
      question: t('addon_popup.faq.faq4_title'),
      answer: t('addon_popup.faq.faq4_content'),
    },
    {
      key: 'faq5',
      question: t('addon_popup.faq.faq5_title'),
      answer: t('addon_popup.faq.faq5_content'),
    },
    {
      key: 'faq6',
      question: t('addon_popup.faq.faq6_title'),
      answer: t('addon_popup.faq.faq6_content'),
    },
    {
      key: 'faq7',
      question: t('addon_popup.faq.faq7_title'),
      answer: t('addon_popup.faq.faq7_content'),
    },
  ];

  const faqListProofread = [
    {
      key: 'faq1',
      question: t('addon_popup.faqProofread.faq1_title'),
      answer: t('addon_popup.faqProofread.faq1_content'),
    },
    {
      key: 'faq2',
      question: t('addon_popup.faqProofread.faq2_title'),
      answer: t('addon_popup.faqProofread.faq2_content'),
    },
    {
      key: 'faq3',
      question: t('addon_popup.faqProofread.faq3_title'),
      answer: t('addon_popup.faqProofread.faq3_content'),
    },
    {
      key: 'faq4',
      question: t('addon_popup.faqProofread.faq4_title'),
      answer: t('addon_popup.faqProofread.faq4_content'),
    },
    {
      key: 'faq5',
      question: t('addon_popup.faqProofread.faq5_title'),
      answer: t('addon_popup.faqProofread.faq5_content'),
    },
    {
      key: 'faq6',
      question: t('addon_popup.faqProofread.faq6_title'),
      answer: t('addon_popup.faqProofread.faq6_content'),
    },
    {
      key: 'faq7',
      question: t('addon_popup.faqProofread.faq7_title'),
      answer: t('addon_popup.faqProofread.faq7_content'),
    },
    {
      key: 'faq8',
      question: t('addon_popup.faqProofread.faq8_title'),
      answer: t('addon_popup.faqProofread.faq8_content'),
    },
    {
      key: 'faq9',
      question: t('addon_popup.faqProofread.faq9_title'),
      answer: t('addon_popup.faqProofread.faq9_content'),
    },
    {
      key: 'faq10',
      question: t('addon_popup.faqProofread.faq10_title'),
      answer: t('addon_popup.faqProofread.faq10_content'),
    },
  ];

  const faqListArtwork = [
    {
      key: 'faq1',
      question: t('addon_popup.faqArtwork.faq1_title'),
      answer: t('addon_popup.faqArtwork.faq1_content'),
    },
    {
      key: 'faq2',
      question: t('addon_popup.faqArtwork.faq2_title'),
      answer: t('addon_popup.faqArtwork.faq2_content'),
    },
    {
      key: 'faq3',
      question: t('addon_popup.faqArtwork.faq3_title'),
      answer: t('addon_popup.faqArtwork.faq3_content'),
    },
    {
      key: 'faq4',
      question: t('addon_popup.faqArtwork.faq4_title'),
      answer: t('addon_popup.faqArtwork.faq4_content'),
    },
    {
      key: 'faq5',
      question: t('addon_popup.faqArtwork.faq5_title'),
      answer: t('addon_popup.faqArtwork.faq5_content'),
    },
    {
      key: 'faq6',
      question: t('addon_popup.faqArtwork.faq6_title'),
      answer: t('addon_popup.faqArtwork.faq6_content'),
    },
    {
      key: 'faq7',
      question: t('addon_popup.faqArtwork.faq7_title'),
      answer: t('addon_popup.faqArtwork.faq7_content'),
    },
    {
      key: 'faq8',
      question: t('addon_popup.faqArtwork.faq8_title'),
      answer: t('addon_popup.faqArtwork.faq8_content'),
    },
    {
      key: 'faq9',
      question: t('addon_popup.faqArtwork.faq9_title'),
      answer: t('addon_popup.faqArtwork.faq9_content'),
    },
    {
      key: 'faq10',
      question: t('addon_popup.faqArtwork.faq10_title'),
      answer: t('addon_popup.faqArtwork.faq10_content'),
    },
    {
      key: 'faq11',
      question: t('addon_popup.faqArtwork.faq11_title'),
      answer: t('addon_popup.faqArtwork.faq11_content'),
    },
  ];

  if (!addons || addons.length === 0) {
    return null;
  }

  return (
    <AddonExpertContainer aria-label="Expert addon cards">
      <SectionSubtitle>
        {t('addon_expert.tit', 'HUMAN EXPERT SERVICES')}
      </SectionSubtitle>
      <SectionTitle>
        {t('addon_expert.title', 'Enhance Your Submission with Experts')}
      </SectionTitle>
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <SectionSubtitle1>
          {t(
            'addon_expert.subtitle',
            'Get expert help for a successful submission. From meeting critical requirements to strengthening overall presentation',
          )}
        </SectionSubtitle1>
      </div>

      <AddonCardsContainer>
        {addons.map((addon) => {
          const addonErrorSection = `addon-${addon.id}`;
          const isErrorForThisAddon =
            errorSection === addonErrorSection && errorScreen;
          const includes = getAddonIncludes(addon.id);
          const description = getAddonDescription(addon.id);

          // const isThisAddonProcessing = processingAddonId === addon.id;
          // const isAnyAddonProcessing = processingAddonId !== null;
          // const shouldDisableButton =
          //   isThisAddonProcessing &&
          //   (uploadState.name === 'loading' || uploadState.name === 'success');

          return (
            <AddonCard
              key={addon.id}
              hasError={isErrorForThisAddon}
              onClick={(event) => handleCardClick(addon, event)}
            >
              <TopSection>
                <IconContainer>
                  <AddonIcon src={addon.icon} alt={addon.title} />
                </IconContainer>
                <CardTitle>
                  {addon.id === 'generic_human_submission_support' ||
                  addon.id === 'human_submission_support'
                    ? t(
                        'addon_expert.expert_submission_support',
                        'Expert Submission Support',
                      )
                    : addon.id === 'generic_human_proofreading'
                    ? t(
                        'addon_expert.standard_proofreading_service',
                        'Standard Proofreading Service',
                      )
                    : addon.id === 'art_work_formatting'
                    ? t(
                        'addon_expert.artwork_formatting_service',
                        'Artwork Formatting Service',
                      )
                    : null}
                </CardTitle>
              </TopSection>

              <Description>{description}</Description>

              <IncludesSection>
                <IncludesTitle>
                  {addon.id === 'art_work_formatting'
                    ? t(
                        'addon_popup.artwork_formatting_includes',
                        'What does the Artwork Formatting service include?',
                      )
                    : t('addon_expert.includes', 'What does this include?')}
                </IncludesTitle>

                <IncludesContainer>
                  <IncludesRow>
                    <CylindricalShape>{includes[0]}</CylindricalShape>
                    <CylindricalShape>{includes[1]}</CylindricalShape>
                  </IncludesRow>
                  <IncludesRow>
                    <CylindricalShape>{includes[2]}</CylindricalShape>
                  </IncludesRow>
                </IncludesContainer>
              </IncludesSection>

              <BottomSection>
                {/* Upload buttons removed - addon selection disabled */}
              </BottomSection>
            </AddonCard>
          );
        })}
      </AddonCardsContainer>

      {showAddonPopup && selectedAddonForPopup && (
        <AddonPopupOverlay onClick={() => setShowAddonPopup(false)}>
          <AddonPopupContainer onClick={(e) => e.stopPropagation()}>
            <AddonPopupHeader>
              <AddonPopupTitle>
                {selectedAddonForPopup.id ===
                  'generic_human_submission_support' ||
                selectedAddonForPopup.id === 'human_submission_support'
                  ? t(
                      'addon_expert.expert_submission_support',
                      'Expert Submission Support',
                    )
                  : selectedAddonForPopup.id === 'generic_human_proofreading'
                  ? t(
                      'addon_expert.standard_proofreading_service',
                      'Standard Proofreading Service',
                    )
                  : selectedAddonForPopup.id === 'art_work_formatting'
                  ? t(
                      'addon_expert.artwork_formatting_service_popup',
                      'Professional Artwork Formatting',
                    )
                  : null}
              </AddonPopupTitle>
              <AddonPopupCloseButton onClick={() => setShowAddonPopup(false)}>
                <Icon name="x" variant="solid" width={36} height={36} />
              </AddonPopupCloseButton>
            </AddonPopupHeader>

            <AddonPopupContent>
              <AddonPopupDescription>
                {selectedAddonForPopup.id === 'generic_human_proofreading' ||
                selectedAddonForPopup.id === 'proofreading'
                  ? t(
                      'addon_popup.human_proofreading_description',
                      'Get a proofreading expert to review your manuscript and correct errors and ensure stylistic consistency in your manuscript. This service includes a thorough review of the AI edits suggested by Paperpal Preflight to determine whether to retain or remove them.',
                    )
                  : selectedAddonForPopup.id === 'art_work_formatting'
                  ? t(
                      'addon_popup.art_work_formatting_description',
                      'Get a formatting expert to ensure your artwork meets journal standards and is ready for submission.',
                    )
                  : t(
                      'addon_popup.ai_human_support_description',
                      'Get an AI-edited document with actionable suggestions along with end-to-end journal submission support. Our Submission Specialist will help you navigate complex journal systems and processes to successfully manage your paper submission and reduce chances of unsubmission.',
                    )}
              </AddonPopupDescription>

              <AddonPopupLine />

              <AddonPopupIncludesTitle>
                {selectedAddonForPopup.id === 'generic_human_proofreading' ||
                selectedAddonForPopup.id === 'proofreading'
                  ? t(
                      'addon_popup.human_proofreading_includes',
                      'What will the proofreading expert do?',
                    )
                  : selectedAddonForPopup.id === 'art_work_formatting'
                  ? t(
                      'addon_popup.artwork_formatting_includes',
                      'What does the Artwork Formatting service include?',
                    )
                  : t(
                      'addon_popup.comprehensive_pack_includes',
                      'What does the Comprehensive Journal Submission Pack include?',
                    )}
              </AddonPopupIncludesTitle>

              <AddonPopupColumnsContainer>
                <AddonPopupColumn>
                  <AddonPopupListContainer>
                    {selectedAddonForPopup.id ===
                      'generic_human_proofreading' ||
                    selectedAddonForPopup.id === 'proofreading' ? (
                      <>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.proofreading_grammar1',
                              'Corrects spelling errors, incorrect punctuations, and other typographical errors.',
                            )}
                          </span>
                        </AddonPopupListItem>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.proofreading_grammar2',
                              'Verify tense usage and subject-verb agreement consistently across the manuscript.',
                            )}
                          </span>
                        </AddonPopupListItem>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.proofreading_grammar3',
                              'Apply language conventions (en-US, en-UK, en-AU) as needed by the journal',
                            )}
                          </span>
                        </AddonPopupListItem>
                      </>
                    ) : selectedAddonForPopup.id === 'art_work_formatting' ? (
                      <>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.artwork_formatting1',
                              'Expert formatting for figure size, resolution, and file extensions as per journal rules',
                            )}
                          </span>
                        </AddonPopupListItem>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.artwork_formatting2',
                              'Optimization of color schemes and overall image clarity for publication',
                            )}
                          </span>
                        </AddonPopupListItem>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.artwork_formatting3',
                              'Precise formatting and labeling of multi-panel figures to meet journal requirements',
                            )}
                          </span>
                        </AddonPopupListItem>
                      </>
                    ) : (
                      <>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <AddonPopupColumnContent>
                            <span>
                              {t(
                                'addon_popup.ai_human_support_suggestions',
                                'AI-edited version of manuscript with actionable suggestions to improve language and resolve technical issues',
                              )}
                            </span>
                          </AddonPopupColumnContent>
                        </AddonPopupListItem>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.submission_specialist_support',
                              'End-to-end Journal Submission Specialist support to ensure your manuscript and all supporting materials are uploaded accurately and completely',
                            )}
                          </span>
                        </AddonPopupListItem>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.reference_formatting',
                              'Manuscript formatting to ensure it meets specific guidelines provided by your target journal.',
                            )}
                          </span>
                        </AddonPopupListItem>
                      </>
                    )}
                  </AddonPopupListContainer>
                </AddonPopupColumn>

                <AddonPopupColumn>
                  <AddonPopupListContainer>
                    {selectedAddonForPopup.id ===
                      'generic_human_proofreading' ||
                    selectedAddonForPopup.id === 'proofreading' ? (
                      <>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.proofreading_grammar4',
                              'Ensure consistency in style, including capitalization, italics, bold, etc.',
                            )}
                          </span>
                        </AddonPopupListItem>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.proofreading_grammar5',
                              'Resolve grammatical errors like incorrect prepositions, idiomatic errors, article usage, etc.',
                            )}
                          </span>
                        </AddonPopupListItem>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.proofreading_grammar6',
                              'Review the AI-edits suggested by Preflight and leave relevant comments.',
                            )}
                          </span>
                        </AddonPopupListItem>
                      </>
                    ) : selectedAddonForPopup.id === 'art_work_formatting' ? (
                      <>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.artwork_formatting4',
                              'Delivery of an artwork preparation report outlining all revisions made',
                            )}
                          </span>
                        </AddonPopupListItem>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.artwork_formatting5',
                              'Receipt of your formatted figures in 2-3 different, journal-ready file formats',
                            )}
                          </span>
                        </AddonPopupListItem>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.artwork_formatting6',
                              'Handling of complex technical guidelines from a wide variety of publishers',
                            )}
                          </span>
                        </AddonPopupListItem>
                      </>
                    ) : (
                      <>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.cover_letter',
                              'Cover letter templatefor you to communicate your research effectively to the journal',
                            )}
                          </span>
                        </AddonPopupListItem>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.upload_manuscript',
                              'Uploading your manuscript on your behalf, along with any supporting documents that are required by the journal or online portal.',
                            )}
                          </span>
                        </AddonPopupListItem>
                        <AddonPopupListItem>
                          <Image
                            src="/green-tick.svg"
                            alt="Green Tick"
                            width={12}
                            height={12}
                            style={{ marginTop: '4px' }}
                          />
                          <span>
                            {t(
                              'addon_popup.one_round_submission',
                              'One round of resubmission support for free, including drafting a new cover letter if needed.',
                            )}
                          </span>
                        </AddonPopupListItem>
                      </>
                    )}
                  </AddonPopupListContainer>
                </AddonPopupColumn>
              </AddonPopupColumnsContainer>

              <AddonPopupFAQToggle onClick={() => handleToggleFAQ('popup_faq')}>
                {showFAQ['popup_faq']
                  ? t('addon_popup.hide_faqs', 'Hide FAQs')
                  : t(
                      'addon_popup.view_faqs',
                      'Still have doubts? check our FAQs',
                    )}
                <Icon
                  name={showFAQ['popup_faq'] ? 'chevronDown' : 'ChevronRight'}
                  variant="regular"
                  width={16}
                  height={16}
                />
              </AddonPopupFAQToggle>

              {selectedAddonForPopup.id === 'generic_human_proofreading' ||
              selectedAddonForPopup.id === 'proofreading'
                ? showFAQ['popup_faq'] && (
                    <AddonPopupFAQContainer>
                      {faqListProofread.map((faq) => (
                        <React.Fragment key={faq.key}>
                          <AddonPopupFAQItem
                            onClick={() => handleToggleFAQ(faq.key)}
                          >
                            <Icon
                              name={
                                showFAQ[faq.key]
                                  ? 'chevronDown'
                                  : 'ChevronRight'
                              }
                              variant="regular"
                              width={16}
                              height={16}
                            />
                            <h4>{faq.question}</h4>
                          </AddonPopupFAQItem>
                          {showFAQ[faq.key] && (
                            <AddonPopupFAQContent>
                              <p>{faq.answer}</p>
                            </AddonPopupFAQContent>
                          )}
                        </React.Fragment>
                      ))}
                    </AddonPopupFAQContainer>
                  )
                : selectedAddonForPopup.id === 'art_work_formatting'
                ? showFAQ['popup_faq'] && (
                    <AddonPopupFAQContainer>
                      {faqListArtwork.map((faq) => (
                        <React.Fragment key={faq.key}>
                          <AddonPopupFAQItem
                            onClick={() => handleToggleFAQ(faq.key)}
                          >
                            <Icon
                              name={
                                showFAQ[faq.key]
                                  ? 'chevronDown'
                                  : 'ChevronRight'
                              }
                              variant="regular"
                              width={16}
                              height={16}
                            />
                            <h4>{faq.question}</h4>
                          </AddonPopupFAQItem>
                          {showFAQ[faq.key] && (
                            <AddonPopupFAQContent>
                              <p>{faq.answer}</p>
                            </AddonPopupFAQContent>
                          )}
                        </React.Fragment>
                      ))}
                    </AddonPopupFAQContainer>
                  )
                : showFAQ['popup_faq'] && (
                    <AddonPopupFAQContainer>
                      {faqList.map((faq) => (
                        <React.Fragment key={faq.key}>
                          <AddonPopupFAQItem
                            onClick={() => handleToggleFAQ(faq.key)}
                          >
                            <Icon
                              name={
                                showFAQ[faq.key]
                                  ? 'chevronDown'
                                  : 'ChevronRight'
                              }
                              variant="regular"
                            />
                            <h4>{faq.question}</h4>
                          </AddonPopupFAQItem>
                          {showFAQ[faq.key] && (
                            <AddonPopupFAQContent>
                              <p>{faq.answer}</p>
                            </AddonPopupFAQContent>
                          )}
                        </React.Fragment>
                      ))}
                    </AddonPopupFAQContainer>
                  )}

              {showAddonPopup &&
                selectedAddonForPopup &&
                processingAddonId === selectedAddonForPopup.id &&
                uploadState.name === 'error' && (
                  <AddonPopupErrorSection>
                    <AddonPopupErrorWrapper>
                      <AddonPopupErrorHeading>
                        {t('addon_expert.errors.upload_error', 'Upload Error')}
                      </AddonPopupErrorHeading>
                      <AddonPopupErrorMessage>
                        <div>
                          <Icon
                            height={16}
                            variant={'regular'}
                            name={'circleInfo'}
                          />
                        </div>

                        <span>
                          {uploadState.error?.json &&
                          typeof uploadState.error.json === 'object' &&
                          'errorReason' in uploadState.error.json
                            ? (uploadState.error.json as UploadErrorResponse)
                                .errorReason
                            : t(
                                'addon_expert.document_reading_error',
                                "We're having trouble reading your document. Please try again.",
                              )}
                        </span>
                      </AddonPopupErrorMessage>
                    </AddonPopupErrorWrapper>
                  </AddonPopupErrorSection>
                )}
            </AddonPopupContent>
            <AddonPopupUploadSection>
              <AddonPopupUploadText>
                {t(
                  'addon_popup.upload_text',
                  'Upload your manuscript for expert assistance',
                )}
              </AddonPopupUploadText>
              {showAddonPopup &&
              selectedAddonForPopup &&
              processingAddonId === selectedAddonForPopup.id &&
              uploadState.name === 'error' ? (
                <UploadButtonNew
                  id={`addon-popup-retry-${selectedAddonForPopup.id}`}
                  data-test-id={`addon-popup-retry-${selectedAddonForPopup.id}`}
                  aria-label={`Retry upload for ${selectedAddonForPopup.title}`}
                  disabled={false}
                  onClick={() => {
                    setOtherSectionClicked(false);
                    //setErrorSection('');
                  }}
                  onFileSelected={(file) => {
                    handleFileSelectedForAddon(file, selectedAddonForPopup.id);
                  }}
                  size="small"
                  isIcon={true}
                  smallSpinner={true}
                  text={t('common.try_again', 'Try again')}
                  isHover={true}
                  trackingProperties={{
                    CTALabel: 'Retry upload for addon from popup',
                    elementName: 'addon_popup_retry',
                    sectionName: `addon_popup_${selectedAddonForPopup.id}`,
                    addonId: selectedAddonForPopup.id,
                  }}
                  css={{
                    width: '240px',
                    height: '40px',
                    backgroundColor: theme.colors.red10.value,
                    color: theme.colors.white.value,
                    border: `1px solid ${theme.colors.red10.value}`,
                    borderRadius: '8px',
                    padding: '10px',
                    fontSize: '14px !important',
                    fontWeight: 600,
                    gap: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'none !important',
                    '&:hover': {
                      backgroundColor: `${theme.colors.red11.value} !important`,
                      boxShadow: 'none !important',
                      color: `${theme.colors.white.value} !important`,
                    },
                  }}
                />
              ) : (
                <UploadButtonNew
                  id={`addon-popup-upload-${selectedAddonForPopup.id}`}
                  data-test-id={`addon-popup-upload-${selectedAddonForPopup.id}`}
                  aria-label={`Upload for ${selectedAddonForPopup.title}`}
                  disabled={
                    processingAddonId === selectedAddonForPopup.id &&
                    (uploadState.name === 'loading' ||
                      uploadState.name === 'success')
                  }
                  disableButSpin={
                    processingAddonId !== null &&
                    processingAddonId !== selectedAddonForPopup.id
                  }
                  onClick={() => {
                    setOtherSectionClicked(false);
                    setErrorSection('');
                  }}
                  onFileSelected={(file) => {
                    handleFileSelectedForAddon(file, selectedAddonForPopup.id);
                  }}
                  size="small"
                  isIcon={true}
                  smallSpinner={true}
                  text={
                    processingAddonId === selectedAddonForPopup.id &&
                    (uploadState.name === 'loading' ||
                      uploadState.name === 'success')
                      ? t('common.processing', 'Processing...')
                      : t('addon_expert.upload_button', 'Upload to get started')
                  }
                  isHover={true}
                  optionalLanguageText={{
                    processing: t('common.processing', 'Processing...'),
                  }}
                  trackingProperties={{
                    CTALabel: 'Upload for addon from popup',
                    elementName: 'addon_popup_upload',
                    sectionName: `addon_popup_${selectedAddonForPopup.id}`,
                    addonId: selectedAddonForPopup.id,
                  }}
                  css={{
                    width: '240px',
                    height: '40px',
                    backgroundColor: theme.colors.blue10.value,
                    color: theme.colors.white.value,
                    border: `1px solid ${theme.colors.blue10.value}`,
                    borderRadius: '8px',
                    padding: '10px',
                    fontSize: '14px !important',
                    fontWeight: 600,
                    gap: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'none !important',
                    '&:hover': {
                      backgroundColor: `${theme.colors.blue11.value} !important`,
                      color: `${theme.colors.white.value} !important`,
                      boxShadow: 'none !important',
                    },
                  }}
                />
              )}
            </AddonPopupUploadSection>
          </AddonPopupContainer>
        </AddonPopupOverlay>
      )}

      {/* <DashedDivider /> */}
    </AddonExpertContainer>
  );
};

const AddonExpertContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  padding: '40px 0',
  alignItems: 'center',
});

const SectionSubtitle = styled('h2', {
  fontSize: '16px',
  fontWeight: '700',
  color: 'rgba(0, 84, 241, 1)',
  textAlign: 'center',
  margin: 0,
});
const SectionTitle = styled('h2', {
  fontSize: '24px',
  fontWeight: '600',
  color: theme.colors.blue12.value,
  textAlign: 'center',
  margin: 0,
  fontFamily: 'IBM Plex Serif',
});
const SectionSubtitle1 = styled('p', {
  fontSize: '20px',
  fontWeight: '400',
  color: theme.colors.blue12.value,
  textAlign: 'center',

  opacity: 1,
  width: '50%',
  marginBottom: '18px',
  marginTop: '4px',
  fontFamily: 'IBM Plex Sans',
});

const AddonCardsContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  gap: '24px',
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%',
  flexWrap: 'wrap',

  '& > *': {
    flex: '0 0 auto',
  },
});

const AddonCard = styled('div', {
  width: '370px',
  border: '1px solid #E5E5E5',
  borderRadius: '20px',
  padding: '24px',
  background: theme.colors.white.value,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  transition: 'all 0.2s ease',
  cursor: 'pointer',

  '&:hover': {
    borderColor: theme.colors.blue10.value,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)',
  },

  variants: {
    hasError: {
      true: {
        height: 'auto',
        minHeight: '420px', // Increased height when error is present
      },
      false: {
        //height: '316px',
      },
    },
  },

  defaultVariants: {
    hasError: false,
  },
});

const TopSection = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const IconContainer = styled('div', {
  width: '50px',
  height: '50px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const AddonIcon = styled('img', {
  width: '50px',
  height: '50px',
  objectFit: 'contain',
});

const CardTitle = styled('div', {
  fontFamily: 'IBM Plex Sans, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '22px',
  color: theme.colors.blue12.value,
  margin: 0,
  width: '75%',
});

const Description = styled('p', {
  fontFamily: 'IBM Plex Sans, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '22px',
  color: theme.colors.blue12.value,
  margin: 0,
});

const IncludesSection = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const IncludesTitle = styled('div', {
  fontFamily: 'IBM Plex Sans, sans-serif',
  fontWeight: 600,
  fontSize: '12px',
  lineHeight: '20px',
  color: theme.colors.blue12.value,
  textAlign: 'left',
});

const IncludesContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '10px',
});

const IncludesRow = styled('div', {
  display: 'flex',
  gap: '10px',
  flexWrap: 'wrap',
});

const CylindricalShape = styled('div', {
  background: 'rgba(230, 240, 255, 1)',
  borderRadius: '30px',
  padding: '2px 10px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  fontSize: '12px',
  fontWeight: 400,
  color: theme.colors.blue12.value,
  whiteSpace: 'nowrap',
});

const BottomSection = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  marginTop: 'auto',
  gap: '8px',
});

// const PurchasedInfo = styled('div', {
//   display: 'flex',
//   alignItems: 'center',
//   gap: '6px',
// });

// const StarIcon = styled('span', {
//   fontSize: '16px',
//   color: theme.colors.green10.value, // Green color
// });

// const PurchasedText = styled('span', {
//   fontFamily: 'IBM Plex Sans, sans-serif',
//   fontWeight: 400,
//   fontSize: '12px',
//   lineHeight: '22px',
//   color: theme.colors.blue12.value,
// });

// const AddonUploadContainer = styled('div', {
//   display: 'flex',
// });

// const ErrorSection = styled('div', {
//   width: '100%',
//   marginBottom: '16px',
// });

// const AddonErrorWrapper = styled('div', {
//   border: `1px solid ${theme.colors.red8.value}`,
//   backgroundColor: theme.colors.red2.value,
//   borderRadius: '8px',
//   padding: '12px 16px',
//   display: 'flex',
//   flexDirection: 'column',
//   gap: '8px',
// });

// const AddonErrorHeading = styled('div', {
//   fontSize: '14px',
//   fontWeight: '600',
//   color: theme.colors.red10.value,
//   fontFamily: 'IBM Plex Sans, sans-serif',
// });

// const AddonErrorMessage = styled('div', {
//   fontSize: '12px',
//   color: theme.colors.red10.value,
//   display: 'flex',
//   alignItems: 'flex-start',
//   gap: '6px',
//   lineHeight: '18px',
//   fontFamily: 'IBM Plex Sans, sans-serif',

//   '& span': {
//     flex: 1,
//   },
// });

const AddonPopupOverlay = styled('div', {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
  backdropFilter: 'blur(1.5px)',
  WebkitBackdropFilter: 'blur(1.5px)',
});

const AddonPopupContainer = styled('div', {
  width: '768px',
  minHeight: '489px',
  backgroundColor: theme.colors.white.value,
  borderRadius: '20px',
  borderBottomWidth: '2px',
  borderLeftWidth: '2px',
  borderStyle: 'solid',
  borderColor: theme.colors.gray6.value,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '90vh',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
});

const AddonPopupHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '24px',
  paddingBottom: '0px',
});

const AddonPopupTitle = styled('h2', {
  fontSize: '20px',
  fontWeight: '600',
  color: theme.colors.blue12.value,
  margin: 0,
});

const AddonPopupCloseButton = styled('button', {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '8px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.blue12.value,
  position: 'absolute',
  top: '10px',
  right: '0',

  '&:hover': {
    //backgroundColor: theme.colors.gray3.value,
    color: theme.colors.blue11.value,
  },
});

const AddonPopupContent = styled('div', {
  flex: 1,
  padding: '24px',
  overflowY: 'auto',
  paddingTop: '12px',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
});

const AddonPopupDescription = styled('div', {
  fontSize: '14px',
  lineHeight: '22px',
  color: theme.colors.blue12.value,
  fontWeight: '400',
});

const AddonPopupFAQToggle = styled('div', {
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '600',
  gap: '4px',
  color: theme.colors.blue12.value,
  fontFamily: 'IBM Plex Sans',
  marginTop: '20px',
  marginBottom: '16px',
  '&:hover': {
    color: theme.colors.blue10.value,
  },
});

const AddonPopupFAQContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});

const AddonPopupFAQItem = styled('div', {
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  paddingBottom: '12px',
  color: theme.colors.blue12.value,
  fontSize: '14px',
  '& h4': {
    fontSize: '12px',
    fontWeight: '500',
    color: theme.colors.blue12.value,
    fontFamily: 'IBM Plex Sans',
    margin: 0,
    marginLeft: '10px',
  },

  '&:hover h4': {
    color: theme.colors.blue10.value,
  },
});

const AddonPopupFAQContent = styled('div', {
  padding: '0 16px 12px 26px',
  fontSize: '12px',
  lineHeight: '18px',
  color: theme.colors.blue12.value,
  fontFamily: 'IBM Plex Sans',
});

const AddonPopupUploadSection = styled('div', {
  backgroundColor: theme.colors.blue4.value,
  borderRadius: '0 0 20px 20px',
  padding: '14px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  width: '100%',

  position: 'sticky',
  bottom: 0,
  marginTop: '0',
  zIndex: 2,
});

const AddonPopupUploadText = styled('div', {
  fontSize: '14px',
  fontWeight: '600',
  color: theme.colors.blue12.value,
  fontFamily: 'IBM Plex Sans',
  flex: 1,
});

const AddonPopupIncludesTitle = styled('h4', {
  fontSize: '14px',
  fontWeight: '600',
  color: theme.colors.blue12.value,
  margin: '0 0 10px 0',
  fontFamily: 'IBM Plex Sans',
});

const AddonPopupColumnsContainer = styled('div', {
  display: 'flex',
  gap: '20px',
  marginTop: '10px',
});

const AddonPopupColumn = styled('div', {
  flex: 1,
});

const AddonPopupListContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
});

const AddonPopupListItem = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',

  '& span': {
    fontSize: '12px',
    lineHeight: '20px',
    color: theme.colors.blue12.value,
    fontFamily: 'IBM Plex Sans',
    flex: 1,
  },
});

const AddonPopupColumnContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1,
});

const AddonPopupLine = styled('div', {
  width: '100%',
  height: '1px',
  background: `repeating-linear-gradient(
    90deg,
    ${theme.colors.gray8.value} 0,
    ${theme.colors.gray8.value} 8px,
    transparent 8px,
    transparent 16px
  )`,
  marginTop: '20px',
  marginBottom: '20px',
});

const AddonPopupErrorSection = styled('div', {
  width: '100%',
  marginBottom: '20px',
});

const AddonPopupErrorWrapper = styled('div', {
  border: `1px solid ${theme.colors.red8.value}`,
  backgroundColor: theme.colors.red2.value,
  borderRadius: '8px',
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const AddonPopupErrorHeading = styled('div', {
  fontSize: '14px',
  fontWeight: '600',
  color: theme.colors.red10.value,
  fontFamily: 'IBM Plex Sans, sans-serif',
});

const AddonPopupErrorMessage = styled('div', {
  fontSize: '12px',
  color: theme.colors.red10.value,
  display: 'flex',
  alignItems: 'flex-start',
  gap: '6px',
  lineHeight: '18px',
  fontFamily: 'IBM Plex Sans, sans-serif',

  '& span': {
    flex: 1,
  },
});
